// DS — dialog, który pyta (artboard 06; anatomia przepisana w sesji 30): potwierdzenie
// z kosztem gemów, błąd z drogą i kodem, akcja nieodwracalna — zamiast okna przeglądarki.
//
// Anatomia języka minimal (reguła 6, artboardy 08a–08c; stopka przepisana regułą 49):
// tytuł 16/600 w przyklejonym nagłówku razem z X · najwyżej JEDNO zdanie · tabela liczb
// (`CostTable`: Koszt / Masz / Zostanie) · akcja na dole. Na 1440 okno 420 px na środku
// z parą „Anuluj” + akcja, na ≤ 640 px TEN SAM komponent jest arkuszem od dołu.
//
// TRZY RODZAJE ARKUSZA (reguła 49, R6) rozstrzyga `kind`:
//   · `action`  — coś kosztuje albo zapisuje: na TELEFONIE zostaje sama akcja główna,
//                 „Anuluj” znika, rezygnacja = X u góry (`R6-02`, `R6-04`);
//   · `destructive` (domyślne przy `tone="danger"`) i `exit` — para w jednym wierszu
//                 ZOSTAJE także na telefonie, bo przy kasowaniu i przy utracie pracy
//                 rezygnacja musi być tak samo oczywista i tak samo blisko kciuka jak
//                 zgoda; bezpieczna akcja ma fokus startowy (`R6-05`).
// Rodzaj rozstrzyga DOM, nie CSS: przycisk schowany `display: none` zostaje w drzewie
// dla czytnika ekranu i dla bramki, a arkusz bez akcji ma NIC na dole, nie „nic widać”.
//
// W oknie KASUJĄCYM akcja główna ma kolor ostrzeżenia i jest wypełniona (reguła 4:
// w oknie jest jedna akcja główna, bo ekran pod spodem jest nieaktywny) — wcześniej
// był to drugorzędny kształt z czerwonym tekstem.
//
// `warning` / `reassurance` / `code` zostają dla ekranów jeszcze nieprzepiętych na
// język minimal (paczki 1–8). Ekran w języku minimal niesie JEDNO zdanie i liczby
// w `rows`; drugie zdanie objaśnienia jest antywzorcem reguły 6.
//
// Bez ikony w kafelku (MAPA §antywzorce). Fokus ląduje na drodze wyjścia, nie na „Usuń”.
//
// Lekcja D10 zostaje zakodowana: wygląd na WŁASNEJ klasie przycisku; wrapper akcji
// ustawia tylko układ.

import type { ReactNode } from 'react'

import Icon from '../media/Icon'
import { useNarrow } from '../../hooks/useNarrow'
import { Button } from '../primitives/Button'
import { CostTable } from './CostTable'
import { Dialog, PHONE_SHEET_QUERY } from './Dialog'

export type ConfirmRow = { k: ReactNode; v: ReactNode; total?: boolean }

export type ConfirmDialogProps = {
  title: string
  /** Jedno zdanie: co się naprawdę stanie. */
  body?: ReactNode
  /** Wiersze kosztu / salda (mono po prawej). Ostatni z `total` ma pogrubioną etykietę. */
  rows?: ConfirmRow[]
  /** Co przepadnie — amber, własne pudełko. */
  warning?: ReactNode
  /** Co wraca — zielona linia pod ostrzeżeniem. */
  reassurance?: ReactNode
  /** Kod błędu dla pomocy (mono 12, --text-muted). */
  code?: string
  /** Akcja nieodwracalna / główna. Nazwij czasownik — „Usuń serię”, nigdy „OK”.
   *  Węzeł, a nie sam łańcuch, od rundy 32-R5: etykieta płatna niesie GLIF monety
   *  przed liczbą (reguła 46), a glif jest elementem. */
  confirmLabel: ReactNode
  /** Droga wyjścia. Ta dostaje fokus. */
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
  /** W toku: przyciski nieaktywne, dialog nie do odrzucenia, główny ze spinnerem. */
  busy?: boolean
  /** Akcja główna widoczna, ale nieaktywna, bez zdania powodu (reguła 19) — np. „Doładuj
   *  tokeny” przy wstrzymanej sprzedaży (PROD2 F2-2). Rezygnacja zostaje czynna. */
  confirmDisabled?: boolean
  /**
   * `danger` (domyślnie) = główny przycisk w tonie niszczącym.
   * `default` = potwierdzenie, które tylko kosztuje (gemy) — czerwony byłby kłamstwem.
   */
  tone?: 'danger' | 'default'
  /**
   * Który z trzech rodzajów arkusza (reguła 49). Domyślnie wynika z `tone`:
   * `danger` → `destructive` (para zostaje), `default` → `action` (na telefonie
   * sama akcja). `exit` dla okien „Wyjść bez zapisu?” / „Przerwać?”, które nie
   * kasują danych, ale odbierają pracę — para zostaje z tego samego powodu.
   */
  kind?: 'action' | 'destructive' | 'exit'
  /** @deprecated ikona w kafelku nie istnieje w systemie 06; prop ignorowany. */
  icon?: string
}

export function ConfirmDialog({
  title, body, rows, warning, reassurance, code, confirmLabel, cancelLabel, onConfirm, onCancel,
  busy = false, confirmDisabled = false, tone = 'danger', kind,
}: ConfirmDialogProps) {
  const phone = useNarrow(PHONE_SHEET_QUERY)
  const rodzaj = kind ?? (tone === 'danger' ? 'destructive' : 'action')
  // Para na dole zostaje przy kasowaniu i przy wyjściu bez zapisu — wszędzie indziej
  // na telefonie rezygnacją jest X u góry.
  const pair = rodzaj !== 'action' || !phone

  return (
    // Bez `onClose` w trakcie: klik w zasłonę mid-request schowałby dialog, a rzecz trwa.
    <Dialog
      onClose={busy ? undefined : onCancel}
      title={title}
      className="ds-confirm mn-dlg"
      footer={(
        <>
          {/* Droga wyjścia jest CICHA (reguła 4) i nadal dostaje fokus: Enter na świeżo
              otwartym oknie nigdy nie kasuje. */}
          {pair ? (
            <Button variant="quiet" data-autofocus onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
          ) : null}
          {/* Wypełniona w obu tonach; `mn-btn-danger` podmienia tylko barwę wypełnienia
              na ostrzegawczą. `btn-danger` zostaje na elemencie, bo to ono mówi reszcie
              systemu (i bramkom), że ta akcja jest niszcząca. */}
          <Button
            variant="primary"
            tone={tone === 'danger' ? 'danger' : 'default'}
            className={tone === 'danger' ? 'mn-btn-danger' : undefined}
            onClick={onConfirm}
            busy={busy}
            disabled={confirmDisabled}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </Button>
        </>
      )}
      footerClassName={pair ? 'dg-foot--pair' : undefined}
    >
      {body && <p className="ds-confirm-text mn-dlg-text">{body}</p>}

      {/* `total` nie maluje już osobno: w tabeli minimal każdy wiersz to para
          etykieta–liczba, a sumę niesie SŁOWO w etykiecie („Koszt”), nie waga. */}
      {rows && rows.length > 0 && <CostTable rows={rows} />}

      {warning && (
        <div className="ds-confirm-warn">
          <Icon name="CircleAlert" size={16} strokeWidth={2} aria-hidden="true" />
          <span>{warning}</span>
        </div>
      )}

      {reassurance && (
        <div className="ds-confirm-ok">
          <Icon name="Check" size={16} strokeWidth={2.5} aria-hidden="true" />
          <span>{reassurance}</span>
        </div>
      )}

      {code && <div className="dg-code">kod: {code}</div>}
    </Dialog>
  )
}

export default ConfirmDialog
